/**
* Handles direct file uploads to S3 in production and local storage in development
* Provides progress tracking and consistent interface across environments
*/
export class S3DirectUpload {
  /**
  * @param {Object} config - Configuration object
  * @param {File} config.file - The file to upload
  * @param {string} [config.presignedUrlPath='/s3_uploaders'] - Endpoint to get upload URL
  * @param {Function} [config.onProgress] - Progress callback (percent: number) => void
  * @param {Function} [config.onSuccess] - Success callback (result: {key, url, contentType, filename}) => void
  * @param {Function} [config.onError] - Error callback (error: Error) => void
  * @param {Object} [config.extraParams] - Additional params to send with presigned URL request
  */
  constructor({
    file,
    presignedUrlPath = '/s3_uploaders',
    onProgress = () => {},
    onSuccess = () => {},
    onError = () => {},
    extraParams = {},
  }) {
    this.file = file
    this.xhr = new XMLHttpRequest() // Used for upload progress tracking
    this.presignedUrlPath = presignedUrlPath
    this.onProgress = onProgress
    this.onSuccess = onSuccess
    this.onError = onError
    this.extraParams = extraParams
  }

  /**
  * Initiates the upload process
  * First gets a presigned URL, then uploads the file
  * @returns {Promise} Resolves with upload result or rejects with error
  */
  async upload() {
    try {
      const presignedData = await this.getPresignedUrl()
      return await this.uploadToS3(presignedData)
    }
    catch (error) {
      this.onError(error)
      throw error
    }
  }

  /**
  * Requests a presigned URL from the server
  * Includes file metadata and any extra parameters
  * @returns {Promise} Resolves with presigned URL data
  */
  async getPresignedUrl() {
    const params = {
      content_type: this.file.type,
      filename: this.file.name,
      ...this.extraParams,
    }
    const response = await fetch(this.presignedUrlPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
      },
      body: JSON.stringify(params),
    })
    if (!response.ok) {
      throw new Error(`Failed to get presigned URL: ${response.statusText}`)
    }
    return response.json()
  }

  /**
  * Handles the actual file upload to either S3 or local storage
  * Routes to different handlers based on environment
  * @param {Object} presignedData - Data from getPresignedUrl
  * @returns {Promise} Resolves with upload result
  */
  uploadToS3(presignedData) {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      // Handle development environment differently
      if (presignedData.environment === 'development') {
        return this.handleDevelopmentUpload(presignedData, resolve, reject)
      }

      // Production S3 upload
      // Add all the presigned fields required by S3
      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value)
      })

      // File must be appended last for S3
      formData.append('file', this.file)

      // Track upload progress
      this.xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100
          this.onProgress(percentComplete)
        }
      })

      // Handle successful upload
      this.xhr.addEventListener('load', () => {
        if (this.xhr.status >= 200 && this.xhr.status < 300) {
          const result = {
            key: presignedData.key,
            url: presignedData.asset_host,
            contentType: this.file.type,
            filename: this.file.name,
          }
          this.onSuccess(result)
          resolve(result)
        }
        else {
          const error = new Error('Upload failed')
          this.onError(error)
          reject(error)
        }
      })

      // Handle network errors
      this.xhr.addEventListener('error', () => {
        const error = new Error('Network error during upload')
        this.onError(error)
        reject(error)
      })

      // Initiate the upload
      this.xhr.open('POST', presignedData.url, true)
      this.xhr.send(formData)
    })
  }

  /**
  * Handles file upload in development environment
  * Uses local storage through CloudfrontDev controller
  * @param {Object} presignedData - Data from getPresignedUrl
  * @param {Function} resolve - Promise resolve function
  * @param {Function} reject - Promise reject function
  */
  handleDevelopmentUpload(presignedData, resolve, reject) {
    const formData = new FormData()
    formData.append('file', this.file)
    formData.append('key', presignedData.key)
    formData.append('type', this.file.type)

    // Track upload progress
    this.xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100
        this.onProgress(percentComplete)
      }
    })

    // Handle successful upload
    this.xhr.addEventListener('load', () => {
      if (this.xhr.status === 201) { // CloudfrontDev returns 201
        const result = JSON.parse(this.xhr.response)
        this.onSuccess({
          key: result.key,
          url: presignedData.asset_host,
          contentType: this.file.type,
          filename: this.file.name,
        })
        resolve(result)
      }
      else {
        const error = new Error('Upload failed')
        this.onError(error)
        reject(error)
      }
    })

    // Handle network errors
    this.xhr.addEventListener('error', () => {
      const error = new Error('Network error during upload')
      this.onError(error)
      reject(error)
    })

    // Initiate the upload
    this.xhr.open('POST', presignedData.url, true)
    this.xhr.send(formData)
  }
}
